import React from "react";

/**
 * SectionContainer — Breite UND Seitenabstand fuer Sektionsinhalte, an einer Stelle.
 *
 * Warum es das gibt (Jan, 5.8.2026): auf einer Seite mischten sich drei
 * Breitenregime — `container px-4`, `container px-4 max-w-4xl` und
 * `container px-6 md:px-12 lg:px-16`. Weil Tailwinds `container` auf
 * Breakpoint-Breiten RASTET, ein `max-w-*` diese Rasterung aber aufhebt, war der
 * sichtbare Rand die Summe aus Zentrier-Einrueckung und Padding — und die kippte
 * je nach Fensterbreite. Auf /angebote gemessen:
 *
 *   1024px: 64, 16, 16, 16, 80, 80, 16, 48   (Streuung 64px)
 *   1280px: 64, 16, 16, 16, 208, 208, 16, 48 (Streuung 192px)
 *
 * Sektionen klebten mit 16px am Rand, waehrend die Nachbarsektion 208px hatte.
 * Der Fehler liess sich mit Padding allein nicht beheben: jede Aenderung
 * verschob ihn nur in einen anderen Fensterbereich.
 *
 * Die Loesung ist, `container` NICHT zu benutzen. Eine feste `max-width` plus
 * Padding waechst monoton mit dem Fenster und springt nie. Die schmalere
 * `text`-Spur ist dadurch immer weiter eingerueckt als `wide` — nie umgekehrt.
 *
 * Auf /angebote nach der Umstellung gemessen (linker Rand je Sektion):
 *
 *    800px: 64, 48, 48, 48, 64, 64, 64, 48
 *    960px: 144, 48, 48, 48, 144, 144, 144, 128
 *   1280px: 64, 112, 112, 112, 304, 304, 304, 48
 *
 * Der Mindestabstand liegt durchgehend bei 48px statt bei 16px. Dass `text`
 * und `wide` auseinanderlaufen, ist gewollt — eine Textspalte SOLL schmaler
 * sein als ein Kartenraster. Gebrochen war vorher die Umkehrung: eine
 * Textsektion stand naeher am Rand als das Raster daneben.
 */

/**
 * Die Spur bestimmt die Lesbarkeit, nicht die Optik:
 *
 * - `text` — Fliesstext und Aufzaehlungen. 768px sind rund 75 Zeichen pro Zeile,
 *   die Grenze, ab der das Auge die naechste Zeile schlechter findet.
 * - `wide` — Karten- und Spaltenraster, die Breite brauchen, aber nicht randlos
 *   laufen sollen.
 * - `full` — nur Padding, keine Breitenbegrenzung. Fuer Baender, Karussells und
 *   Hero-Bereiche, die bewusst bis an den Rand gehen.
 */
export type SectionContainerWidth = "text" | "wide" | "full";

export type SectionContainerProps = {
    width?: SectionContainerWidth;
    /** Zusaetzliche Klassen — Abstaende nach oben/unten, Textausrichtung, Raster. */
    className?: string;
    children: React.ReactNode;
    /** Sprungziel fuer Links von aussen; setzt zugleich `scroll-mt`. */
    id?: string;
    "data-testid"?: string;
};

/*
 * `mx-auto` zentriert, `w-full` haelt die Box unterhalb der max-width auf voller
 * Breite. Das Padding ist dasselbe wie in der zentralen Container-Regel
 * (styles/index.css), damit Sektionen, die noch nicht umgestellt sind, in der
 * gleichen Flucht stehen.
 */
const WIDTH: Record<SectionContainerWidth, string> = {
    text: "max-w-3xl",
    wide: "max-w-6xl",
    full: "",
};

export const SectionContainer: React.FC<SectionContainerProps> = ({
    width = "wide",
    className = "",
    children,
    id,
    "data-testid": testId,
}) => (
    <div
        id={id}
        data-testid={testId}
        /*
         * `md:px-12` (48px) statt der naheliegenden 32px: zwischen 768px und
         * 1152px ist die `wide`-Spur BREITER als das Fenster, die max-width
         * greift also nicht und das Padding traegt den Rand allein. Mit 32px
         * stuenden Kartenraster dort sichtbar enger als auf jeder anderen
         * Breite (auf /angebote gemessen: 32px gegen 128px der Textspur).
         */
        className={["mx-auto w-full px-4 md:px-12", WIDTH[width], id ? "scroll-mt-24" : "", className]
            .filter(Boolean)
            .join(" ")}
    >
        {children}
    </div>
);

export default SectionContainer;
